import React from 'react';
import { ArrowRight } from 'lucide-react';
import { Button } from './Button';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';

export const EcoFriendlySection: React.FC = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  return (
    <section className="py-20 bg-[#0A0A0A]" ref={ref}>
      <div className="container mx-auto px-4">
        <motion.div 
          className="relative w-full rounded-[30px] overflow-hidden min-h-[500px] md:min-h-[600px] flex items-center"
          style={{
            backgroundImage: `url(/Images/h_tech.webp)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8 }}
        >
          {/* Dark Overlay for readability */}
          <div className="absolute inset-0 bg-black/60" />

          {/* Animated gradient overlay */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-[#CA1167]/20 to-[#F47749]/20"
            animate={{
              opacity: [0.2, 0.4, 0.2]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }}
          />

          {/* Floating particles */}
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-white/30 rounded-full"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -50, 0],
                opacity: [0.2, 0.8, 0.2],
                scale: [1, 1.5, 1]
              }}
              transition={{
                duration: 3 + i * 0.5,
                repeat: Infinity,
                delay: i * 0.4
              }}
            />
          ))}

          <div className="relative z-10 grid lg:grid-cols-2 gap-12 p-6 md:p-12 lg:p-16 items-center w-full">
            {/* Text Content */}
            <motion.div 
              className="max-w-2xl"
              initial={{ opacity: 0, x: -50 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <motion.h2 
                className="font-heading font-bold text-[32px] md:text-[52px] leading-[40px] md:leading-[67.6px] mb-6"
                style={{ color: '#FFFFFFAB' }}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                Eco-friendly boards and recyclable, responsible packaging.
              </motion.h2>
              <motion.p 
                className="font-instrument font-medium text-[16px] md:text-[24px] leading-[24px] md:leading-[31.2px] text-gray-300 mb-10"
                initial={{ opacity: 0 }}
                animate={isInView ? { opacity: 1 } : {}}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                Advanced printing, precision die-cutting, and high-speed finishing
              </motion.p>

              <Link to="/technology" className="flex items-center gap-4 group">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button className="bg-[linear-gradient(88.69deg,#CA1167_-1.27%,#F47749_98.89%)] text-white rounded-full px-8 py-4 h-auto font-instrument font-medium text-[20.51px] leading-[20.51px] group-hover:shadow-lg transition-all duration-300">
                    Know More About Our Machinery
                  </Button>
                </motion.div>
                <motion.button 
                  className="cursor-target w-[50px] h-[50px] rounded-full bg-[linear-gradient(88.69deg,#CA1167_-1.27%,#F47749_98.89%)] flex items-center justify-center text-white group-hover:shadow-lg transition-all duration-300"
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  animate={{
                    boxShadow: [
                      '0 0 0 0 rgba(244, 119, 73, 0.4)',
                      '0 0 0 20px rgba(244, 119, 73, 0)',
                    ]
                  }}
                  transition={{
                    boxShadow: {
                      duration: 2,
                      repeat: Infinity,
                    }
                  }}
                >
                   <ArrowRight size={24} />
                </motion.button>
              </Link>
            </motion.div>

            {/* Right Side - Animated decorative element */}
            <motion.div 
              className="hidden lg:flex justify-end items-center"
              initial={{ opacity: 0, x: 50 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <motion.div
                className="relative w-64 h-64"
                animate={{
                  rotate: [0, 360]
                }}
                transition={{
                  duration: 20,
                  repeat: Infinity,
                  ease: 'linear'
                }}
              >
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-[#CA1167]/30 to-[#F47749]/30 blur-3xl" />
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
